import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TranslationProvider } from './context/TranslationContext';
import LandingPage from './components/LandingPage';
import LoginPage from './components/LoginPage';
import ModuleSelectionPage from './pages/ModuleSelectionPage';
import { getSafeRedirectUrl } from './pages/moduleSelectionUtils';
import SsoCallbackPage from './pages/SsoCallbackPage';
import LogoutPage from './pages/LogoutPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import GeneralTerms from './pages/GeneralTerms';
import './App.css';

// Main App Content
const AppContent: React.FC = () => {
    const { isAuthenticated, isLoading, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Handle login callback
    const handleLogin = (userType: 'user' | 'admin') => {
        // Check if there's a redirect URL and validate to avoid open-redirect / token leakage
        const urlParams = new URLSearchParams(location.search);
        const redirectUrl = getSafeRedirectUrl(urlParams.get('redirect'));

        if (redirectUrl) {
            // Get the token and redirect to the original URL
            const token = localStorage.getItem('auth_token');
            if (token) {
                const separator = redirectUrl.includes('?') ? '&' : '?';
                window.location.href = `${redirectUrl}${separator}token=${token}`;
                return;
            }
        }

        // Default: go to modules page
        navigate('/modules');
    };

    if (isLoading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <Routes>
            {/* Landing Page */}
            <Route
                path="/"
                element={
                    isAuthenticated ?
                        <Navigate to="/modules" replace /> :
                        <LandingPage onLogin={() => navigate('/signin')} />
                }
            />

            {/* Login/Signup Page */}
            <Route
                path="/signin"
                element={
                    isAuthenticated ?
                        <Navigate to="/modules" replace /> :
                        <LoginPage onLogin={handleLogin} onBack={() => navigate('/')} />
                }
            />

            {/* SSO Callback */}
            <Route path="/auth/sso-callback" element={<SsoCallbackPage />} />

            {/* Cross-app logout endpoint (clears portal session, then home) */}
            <Route path="/logout" element={<LogoutPage />} />

            {/* Static Pages */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<GeneralTerms />} />

            {/* Module Selection (Protected) */}
            <Route
                path="/modules"
                element={
                    isAuthenticated ?
                        <ModuleSelectionPage /> :
                        <Navigate to="/signin" replace />
                }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};

function App() {
    return (
        <Router>
            <TranslationProvider>
                <AuthProvider>
                    <div className="app">
                        <AppContent />
                    </div>
                </AuthProvider>
            </TranslationProvider>
        </Router>
    );
}

export default App;
